import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTerminal, faKey, faPencilAlt, faRocket, faTimesCircle, faCheckCircle, faClock } from '@fortawesome/free-solid-svg-icons';
import {
  SectionTitle, BlockHeadingIcon, CodeBlock, InfoCard, MethodBadge, Panel, useApiDocs,
} from '../../../components/apidocs';
import { PLAYGROUND_ENDPOINTS, ORDER_FORM_FIELDS, PAYOUT_FORM_FIELDS, NO_PARAM_ENDPOINTS } from '../data/playground';
import type { PlaygroundField } from '../data/playground';
import type { PlaygroundController } from '../hooks/usePlayground';

const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 text-sm font-mono focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-slate-600 outline-none';
const LABEL_CLASS = 'block text-xs font-medium text-gray-600 dark:text-slate-300 mb-1';

/** ช่องกรอกหนึ่งช่องของฟอร์ม playground */
const TextField: React.FC<{
  label: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
}> = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div>
    <label className={LABEL_CLASS}>{label}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={INPUT_CLASS}
    />
  </div>
);

/** ฟอร์มที่สร้างจากรายการ field (create-order / create-payout) */
const FieldsForm: React.FC<{
  fields: PlaygroundField[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}> = ({ fields, values, onChange }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    {fields.map(f => (
      <TextField
        key={f.key}
        label={<>{f.label} {f.required && <span className="text-rose-500">*</span>}</>}
        type={f.type || 'text'}
        value={values[f.key] || ''}
        onChange={value => onChange(f.key, value)}
      />
    ))}
  </div>
);

const PlaygroundSection: React.FC<{ playground: PlaygroundController }> = ({ playground }) => {
  const { apiKey, baseUrl } = useApiDocs();
  const {
    endpoint, selectEndpoint, loading, response, error,
    apiSecret, setApiSecret, orderNumber, setOrderNumber, payoutNumber, setPayoutNumber,
    fields, setFields, payoutFields, setPayoutFields, verifySlipFields, setVerifySlipFields,
    execute, buildCurl,
  } = playground;

  const selected = PLAYGROUND_ENDPOINTS.find(e => e.id === endpoint);

  return (
    <div>
      <SectionTitle icon={faTerminal} iconColor="bg-gradient-to-br from-cyan-400 to-blue-500">
        API Playground
      </SectionTitle>
      <p className="text-gray-500 dark:text-slate-400 mb-6 text-sm">ทดสอบ API ได้แบบ Real-time โดยใช้ API Key ของคุณ เหมือนมี Postman ในตัว</p>

      <InfoCard type="warning" title="Live API">
        การทดสอบจะเรียก API จริง เช่น Create Order จะสร้าง order จริงในระบบ กรุณาใช้อย่างระมัดระวัง
      </InfoCard>

      {/* API Credentials */}
      <Panel>
        <BlockHeadingIcon icon={faKey} iconClassName="text-amber-500 text-xs">API Credentials</BlockHeadingIcon>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={LABEL_CLASS}>API Key</label>
            <input
              type="text"
              value={apiKey}
              readOnly
              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-600 text-sm font-mono text-gray-500 dark:text-slate-400 cursor-not-allowed"
            />
          </div>
          <TextField
            label={<>
              API Secret <span className="text-rose-500">*</span>
              <span className="text-gray-400 font-normal ml-1">(ดูได้จากหน้า API Keys)</span>
            </>}
            type="password"
            value={apiSecret}
            onChange={setApiSecret}
            placeholder="sk_your_api_secret"
          />
        </div>
      </Panel>

      {/* Endpoint Selector */}
      <div className="mb-6">
        <label className="block text-sm font-bold text-gray-700 dark:text-slate-200 mb-2">Select Endpoint</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {PLAYGROUND_ENDPOINTS.map(ep => (
            <button
              key={ep.id}
              onClick={() => selectEndpoint(ep.id)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-xs font-medium border transition-all ${
                endpoint === ep.id
                  ? 'border-indigo-300 bg-indigo-50 text-indigo-700 shadow-sm'
                  : 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600'
              }`}
            >
              <MethodBadge method={ep.method} />
              <span className="truncate">{ep.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* URL Preview */}
      {selected && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-[#0d1117] mb-6 border border-gray-800">
          <MethodBadge method={selected.method} />
          <code className="text-emerald-400 text-sm font-mono flex-1 truncate">{baseUrl}{selected.path}</code>
        </div>
      )}

      {/* Request Fields */}
      <Panel>
        <BlockHeadingIcon icon={faPencilAlt} margin="mb-4">Request Parameters</BlockHeadingIcon>

        {(endpoint === 'create-order' || endpoint === 'create-slip-order') && (
          <FieldsForm
            fields={ORDER_FORM_FIELDS}
            values={fields}
            onChange={(key, value) => setFields(prev => ({ ...prev, [key]: value }))}
          />
        )}

        {endpoint === 'get-order' && (
          <TextField
            label={<>Order Number <span className="text-rose-500">*</span></>}
            value={orderNumber}
            onChange={setOrderNumber}
            placeholder="ORD_abc123xyz"
          />
        )}

        {endpoint === 'create-payout' && (
          <FieldsForm
            fields={PAYOUT_FORM_FIELDS}
            values={payoutFields}
            onChange={(key, value) => setPayoutFields(prev => ({ ...prev, [key]: value }))}
          />
        )}

        {endpoint === 'get-payout' && (
          <TextField
            label={<>Payout Number <span className="text-rose-500">*</span></>}
            value={payoutNumber}
            onChange={setPayoutNumber}
            placeholder="PAYOUT20251009001"
          />
        )}

        {NO_PARAM_ENDPOINTS.includes(endpoint) && (
          <p className="text-gray-400 dark:text-slate-500 text-sm italic">ไม่ต้องกรอก parameter — กด Send Request ได้เลย</p>
        )}

        {endpoint === 'verify-slip' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <TextField
                label={<>Order Number <span className="text-rose-500">*</span></>}
                value={verifySlipFields.order_number || ''}
                onChange={value => setVerifySlipFields(prev => ({ ...prev, order_number: value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <TextField
                label={<>QR String <span className="text-amber-500 text-xs font-normal">(one of)</span></>}
                placeholder="0002010102..."
                value={verifySlipFields.qr_string || ''}
                onChange={value => setVerifySlipFields(prev => ({ ...prev, qr_string: value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={LABEL_CLASS}>
                imageBase64 <span className="text-amber-500 text-xs font-normal">(one of)</span>
              </label>
              <textarea
                rows={3}
                placeholder="data:image/jpeg;base64,/9j/..."
                value={verifySlipFields.imageBase64 || ''}
                onChange={e => setVerifySlipFields(prev => ({ ...prev, imageBase64: e.target.value }))}
                className={`${INPUT_CLASS} resize-none`}
              />
            </div>
          </div>
        )}
      </Panel>

      {/* cURL Preview */}
      <div className="mb-6">
        <BlockHeadingIcon icon={faTerminal}>cURL Command</BlockHeadingIcon>
        <CodeBlock code={buildCurl()} label="bash" id="pg-curl" />
      </div>

      {/* Send Button */}
      <button
        onClick={execute}
        disabled={loading}
        className={`w-full py-3 rounded-xl font-bold text-sm text-white shadow-lg transition-all flex items-center justify-center gap-2 ${
          loading
            ? 'bg-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 hover:shadow-xl hover:-translate-y-0.5'
        }`}
      >
        {loading ? (
          <>
            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Sending...
          </>
        ) : (
          <>
            <FontAwesomeIcon icon={faRocket} /> Send Request
          </>
        )}
      </button>

      {/* Response */}
      {error && (
        <div className="mt-6 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-xl p-4">
          <div className="flex items-center gap-2 text-rose-700 font-bold text-sm mb-1">
            <FontAwesomeIcon icon={faTimesCircle} /> Network Error
          </div>
          <p className="text-rose-600 text-sm">{error}</p>
        </div>
      )}

      {response && (
        <div className="mt-6">
          <div className="flex items-center gap-3 mb-3">
            <h3 className="font-bold text-gray-700 dark:text-slate-200 text-sm flex items-center gap-2">
              <FontAwesomeIcon icon={faCheckCircle} className="text-gray-400 dark:text-slate-500 text-xs" /> Response
            </h3>
            <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${
              response.status >= 200 && response.status < 300
                ? 'bg-emerald-100 text-emerald-700'
                : response.status >= 400
                ? 'bg-rose-100 text-rose-700'
                : 'bg-amber-100 text-amber-700'
            }`}>
              {response.status}
            </span>
            <span className="text-xs text-gray-400 flex items-center gap-1">
              <FontAwesomeIcon icon={faClock} className="text-[10px]" /> {response.time}ms
            </span>
          </div>
          <CodeBlock
            code={JSON.stringify(response.data, null, 2)}
            label="response.json"
            id="pg-response"
          />
        </div>
      )}
    </div>
  );
};

export default PlaygroundSection;
